/**
 * Single source of truth for "does this form supply the assets required
 * for a confirmed order?"
 *
 * Multi-leg orders may keep their assets (tractor, trailer, trailer type,
 * trip type) at either the order level (parent mirror, `use_same_*` true)
 * or on each leg (`use_same_*` false). Historically both the Create dialog
 * and Edit dialog re-implemented these presence checks inline against the
 * parent-level fields only, which quietly broke every time we added a
 * per-leg toggle. Any confirm-time asset check should route through this
 * helper so the two dialogs stay in lock-step.
 */
import type { OrderFormData, LegData } from "@/components/orders/orderFormTypes";
import type { TrailerTypeRecord } from "@/hooks/useTrailerTypes";
import { bobtailPredicate, missingConfirmRequirements } from "@/lib/confirm-readiness";
import { legMirrorsParentEquipment } from "@/lib/confirm-plan-legs";

type TrailerTypeLite = Pick<TrailerTypeRecord, "id" | "kind">;

/** True when the form assigns trailers/tractors on each leg individually. */
export function isPerLegAssets(fd: OrderFormData): boolean {
  return fd.order_type === "multi-leg" && fd.use_same_assets_for_all_legs === false;
}

/** True when the form assigns trip type on each leg individually. */
export function isPerLegTripType(fd: OrderFormData): boolean {
  return fd.order_type === "multi-leg" && fd.use_same_trip_type_for_all_legs === false;
}

function legAssetsFor(fd: OrderFormData): LegData[] {
  return fd.legs ?? [];
}

export interface ConfirmAssetsCheck {
  /** All required assets are present. */
  ok: boolean;
  /** True when the missing pieces live on legs, not on the parent. Callers
   *  can use this to route the user's attention to the right field. */
  perLeg: boolean;
}

/**
 * Does the form satisfy the "confirmed order needs a tractor and a trailer"
 * rule? Handles both per-leg and parent-mirror modes; treats bobtail trailer
 * types as trailer-not-required.
 *
 * Delegates the actual presence rules to `src/lib/confirm-readiness.ts`, the
 * shared implementation used by the Order Detail button and the write-path
 * pre-flight. Trip type is validated separately here by `hasConfirmTripType`,
 * so it is excluded from this check.
 */
export function hasConfirmAssets(
  fd: OrderFormData,
  trailerTypes: TrailerTypeLite[] | undefined,
): ConfirmAssetsCheck {
  const isBobtailTrailerType = bobtailPredicate(trailerTypes);
  const perLeg = isPerLegAssets(fd);
  const missing = missingConfirmRequirements({
    checkTripType: false,
    isBobtailTrailerType,
    tractorId: fd.assigned_tractor_id,
    trailerId: fd.trailer_id,
    trailerTypeId: fd.trailer_type_id,
    tripType: fd.trip_type,
    operatorId: fd.assigned_operator_id,
    legs: perLeg
      ? legAssetsFor(fd).map((l, i) => ({
          tractorId: l.assigned_tractor_id,
          trailerId: l.trailer_id,
          trailerTypeId: l.trailer_type_id,
          // Trip type falls back to the order's on every leg.
          tripType: l.trip_type || fd.trip_type || null,
          operatorId:
            l.assigned_operator_id ||
            (legMirrorsParentEquipment(i + 1, legAssetsFor(fd).length)
              ? fd.assigned_operator_id
              : null) ||
            null,
        }))
      : undefined,
  });

  return { ok: missing.length === 0, perLeg };
}

/** Trailer-type presence check. Confirmed orders require it wherever the
 *  assets live; bobtail counts as trailer type set. */
export function hasConfirmTrailerType(
  fd: OrderFormData,
  _trailerTypes: TrailerTypeLite[] | undefined,
): boolean {
  if (isPerLegAssets(fd)) {
    return (fd.legs ?? []).every((l) => !!l.trailer_type_id);
  }
  return !!fd.trailer_type_id;
}

/** Trip-type presence check. */
export function hasConfirmTripType(fd: OrderFormData): boolean {
  if (isPerLegTripType(fd)) {
    return (fd.legs ?? []).every((l) => !!(l.trip_type ?? "").trim());
  }
  return !!(fd.trip_type ?? "").trim();
}

/** Equipment snapshot for the "don't strip a live trip's assets" gate. */
export interface AssetSnapshot {
  tractorId?: string | null;
  trailerId?: string | null;
}

export interface AssetRemovalInput {
  /** Values as currently stored on the order/legs. */
  before: { parent: AssetSnapshot; legs?: AssetSnapshot[] };
  /** Values the form is about to save. */
  after: { parent: AssetSnapshot; legs?: AssetSnapshot[] };
  /** True when the form assigns assets per leg. */
  perLeg: boolean;
  /**
   * Which parts own a live external trip. Each leg is gated only by its OWN
   * trip — a live trip on leg 1 never blocks clearing equipment on leg 2.
   * The parent columns mirror leg 1, so `parent` is leg 1's trip. Omitted =
   * every part is treated as live (legacy behaviour).
   */
  liveTrip?: { parent: boolean; legs?: boolean[] };
}

function isSet(v: string | null | undefined): boolean {
  return !!(v ?? "").trim();
}

/** Did this edit clear a tractor/trailer that was previously assigned? */
function clearsAny(before: AssetSnapshot, after: AssetSnapshot): boolean {
  return (
    (isSet(before.tractorId) && !isSet(after.tractorId)) ||
    (isSet(before.trailerId) && !isSet(after.trailerId))
  );
}

/**
 * Trip-integrity gate for orders with a live external trip: block a save only
 * when it actually *removes* equipment that was already on the order. An order
 * that was saved without a trailer stays saveable — adding a truck to it is an
 * improvement, not a removal. Absence-only checks (`hasConfirmAssets`) wrongly
 * rejected those edits.
 */
export function removesConfirmedAssets(input: AssetRemovalInput): boolean {
  const live = input.liveTrip;
  const parentLive = live ? live.parent : true;
  const legLive = (i: number) => (live ? !!live.legs?.[i] : true);
  if (parentLive && clearsAny(input.before.parent, input.after.parent)) return true;
  if (!input.perLeg) return false;
  const beforeLegs = input.before.legs ?? [];
  const afterLegs = input.after.legs ?? [];
  return beforeLegs.some((b, i) => legLive(i) && clearsAny(b, afterLegs[i] ?? {}));
}